import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import SportsMmaIcon from '@mui/icons-material/SportsMma';
import RefreshIcon from '@mui/icons-material/Refresh';
import agent from '../../agent';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import useFreaksT from './useFreaksT';
import FreaksAvatar from './FreaksAvatar';
import FreaksBotBadge from './FreaksBotBadge';
import { contextIcon } from './contextIcon';
import { actionSx, FREAKS_RED, freaksSx as s } from './freaksStyles';

// Crims-style assaults, Freakland-style: challenge a freak with similar respect. Newcomers are protected
// (and only get bots); challenging the same freak a lot in a week pays less (target.loot).
export default function FreaksFight({ state, energy, disabled, onChallenge }) {
  const { t, language } = useFreaksT();
  const [targets, setTargets] = useState(null);
  const [newcomerUntil, setNewcomerUntil] = useState(null);

  const load = useCallback(async () => {
    setTargets(null);
    try {
      const data = await agent.TheFreaks.targets();
      setTargets(data.targets);
      setNewcomerUntil(data.newcomerUntil);
    } catch {
      setTargets([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fight = async (target) => {
    // one challenge per freak per day: drop them from the list once done
    if (await onChallenge(target)) setTargets((list) => (list ?? []).filter((item) => item.username !== target.username));
  };

  return (
    <Box data-testid="freaks-fight">
      <Typography sx={s.intro}>{t('theFreaks.fight.intro')}</Typography>
      {newcomerUntil ? (
        <Box sx={s.card} data-testid="freaks-newcomer">
          <Typography sx={s.small}>
            {t('theFreaks.fight.newcomer', { date: new Date(newcomerUntil).toLocaleDateString(language) })}
          </Typography>
        </Box>
      ) : null}
      <Typography sx={{ ...s.bold, mb: 1, color: 'white' }}>
        🥊 {t('theFreaks.fight.record', { won: state.record.challengesWon, lost: state.record.challengesLost })}
      </Typography>
      {targets === null ? <Box sx={{ textAlign: 'center', mt: 2 }}><CircularProgress sx={{ color: DEFAULT_THEME_COLOR }}/></Box> : null}
      {targets && !targets.length ? <Typography sx={s.empty}>{t('theFreaks.fight.empty')}</Typography> : null}
      {(targets ?? []).map((target) => (
        <Box key={target.username} sx={{ ...s.card, ...s.between }}>
          <FreaksAvatar username={target.username} imageUrl={target.imageUrl}/>
          <Box sx={s.grow}>
            <Box sx={s.row}>
              <Typography noWrap sx={{ ...s.bold, flexShrink: 1 }}>{target.isBot ? target.username : `@${target.username}`}</Typography>
              {target.isBot ? <FreaksBotBadge/> : null}
            </Box>
            <Box sx={{ ...s.row, gap: 0.5 }}>
              {contextIcon(target.context, { fontSize: 14, color: '#888' })}
              <Typography sx={{ ...s.small, ...s.muted }}>
                {t('theFreaks.level', { level: target.level })} · {target.respect} {t('theFreaks.respect').toLowerCase()}
              </Typography>
            </Box>
            {target.loot !== undefined && target.loot < 1 ? (
              <Typography sx={{ ...s.small, color: FREAKS_RED }} data-testid={`freaks-loot-${target.username}`}>
                {t('theFreaks.fight.loot', { percent: Math.round(target.loot * 100) })}
              </Typography>
            ) : null}
          </Box>
          <Button
            data-testid={`freaks-challenge-${target.username}`}
            startIcon={<SportsMmaIcon/>}
            disabled={disabled || energy < state.challengeEnergy}
            onClick={() => fight(target)}
            sx={actionSx(FREAKS_RED)}
          >
            {t('theFreaks.fight.challenge')}
          </Button>
        </Box>
      ))}
      <Typography sx={{ ...s.small, ...s.muted, textAlign: 'center' }}>
        ⚡ {t('theFreaks.fight.cost', { energy: state.challengeEnergy })}
      </Typography>
      <Box sx={{ textAlign: 'center' }}>
        <Button startIcon={<RefreshIcon/>} onClick={load} sx={{ minHeight: 44, color: DEFAULT_THEME_COLOR, textTransform: 'none' }}>
          {t('theFreaks.fight.refresh')}
        </Button>
      </Box>
    </Box>
  );
}
